import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRecipes, useSwipes, useFamilyMembers } from '../hooks/useSupabase';
import { filterByDietary } from '../lib/dietary';
import { trackEvent } from '../lib/analytics';
import { detectHouseholdMatch, getShownMatchIds, markMatchShown } from '../lib/matchEngine';
import { LoadingSpinner, EmptyState, ErrorState } from './ui/StateViews';
import SwipeCard from './SwipeCard';
import SwipeCoach from './SwipeCoach';
import RecipeDetail from './RecipeDetail';
import MatchMoment from './MatchMoment';

const MAX_UNDO_DEPTH = 3;

export default function SwipeScreen() {
  const { householdId, currentMember } = useAuth();
  const { recipes, loading: recipesLoading, error: recipesError, refetch: recipesRefetch } = useRecipes(householdId);
  const { swipes, rawSwipes, loading: swipesLoading, error: swipesError, recordSwipe, refetch: swipesRefetch } = useSwipes(householdId);
  const { members } = useFamilyMembers(householdId);
  // First-run swipe coach: shown once per device until dismissed.
  const [showCoach, setShowCoach] = useState(
    () => typeof window !== 'undefined' && !localStorage.getItem('dinner-league-swipe-coach-seen')
  );
  const dismissCoach = useCallback(() => {
    setShowCoach(false);
    try { localStorage.setItem('dinner-league-swipe-coach-seen', '1'); } catch { /* ignore */ }
  }, []);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [detailRecipe, setDetailRecipe] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [matchMoment, setMatchMoment] = useState(null);

  const loading = recipesLoading || swipesLoading;

  const unrated = useMemo(() => {
    if (loading) return [];
    const swipesForMember = currentMember ? (swipes[currentMember.id] || {}) : {};
    const compatible = filterByDietary(recipes, currentMember?.allergies);
    return compatible.filter(r => !(r.id in swipesForMember));
  }, [recipes, swipes, loading, currentMember]);

  const currentRecipe = unrated[sessionIndex] || null;
  const nextRecipe = unrated[sessionIndex + 1] || null;
  const totalToRate = unrated.length + sessionIndex;
  const progress = totalToRate > 0 ? sessionIndex / totalToRate : 1;

  const handleSwipe = useCallback((liked) => {
    if (!currentMember || !currentRecipe) return;
    // Push onto undo stack before recording
    setUndoStack(prev => {
      const next = [...prev, { recipeId: currentRecipe.id, liked }];
      return next.length > MAX_UNDO_DEPTH ? next.slice(-MAX_UNDO_DEPTH) : next;
    });
    recordSwipe(currentMember.id, currentRecipe.id, liked);
    trackEvent('Swipe', { direction: liked ? 'like' : 'dislike' });
    setSessionIndex(prev => prev + 1);

    const updatedSwipes = [...(rawSwipes || []), { member_id: currentMember.id, recipe_id: currentRecipe.id, liked, swiped_at: new Date().toISOString() }];

    if (liked && !getShownMatchIds().has(currentRecipe.id)) {
      const match = detectHouseholdMatch({
        members,
        recipes,
        rawSwipes: updatedSwipes,
        recipeId: currentRecipe.id,
      });
      if (match) {
        markMatchShown(currentRecipe.id);
        setMatchMoment({ ...match, triggerMember: currentMember });
        trackEvent('Match', { recipeId: currentRecipe.id, reason: match.reason });
      }
    }
  }, [currentMember, currentRecipe, recordSwipe, rawSwipes, members, recipes]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || sessionIndex <= 0) return;
    setUndoStack(prev => prev.slice(0, -1));
    setSessionIndex(prev => prev - 1);
  }, [undoStack.length, sessionIndex]);

  // Keyboard shortcuts for desktop
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleSwipe(false); // Pass
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleSwipe(true); // Like
      } else if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSwipe, handleUndo]);

  const error = recipesError || swipesError;
  const refetchAll = () => { recipesRefetch(); swipesRefetch(); };

  if (loading) return <LoadingSpinner message="Loading recipes..." />;
  if (error) return <ErrorState message={error} onRetry={refetchAll} />;

  if (!currentMember) {
    return (
      <EmptyState
        emoji="👤"
        title="No member selected"
        description="Select a family member on the home screen to start rating dinners."
        action="Go to home"
        to="/"
      />
    );
  }

  if (!currentRecipe) {
    return (
      <EmptyState mascot title="All done!" description={`${currentMember.name.split(' ')[0]} has rated all ${recipes.length} recipes. New recipes show up here when added.`} action="Back to home" to="/" />
    );
  }

  // SVG progress ring
  const ringRadius = 22;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference - (progress * ringCircumference);

  return (
    <div className="flex flex-col items-center gap-5 lg:gap-8">
      {/* Header with circular progress */}
      <div className="w-full flex items-center justify-between lg:max-w-md lg:mx-auto">
        <div>
          <h2 className="font-display text-lg lg:text-xl font-semibold text-ink">Recipes</h2>
          <p className="font-body text-xs lg:text-sm text-ink-muted mt-0.5">Tell us which dinners your family likes</p>
        </div>
        <div className="relative w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center">
          <svg className="w-14 h-14 lg:w-16 lg:h-16 -rotate-90" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r={ringRadius} fill="none" stroke="#233145" strokeWidth="3" />
            <circle
              cx="28" cy="28" r={ringRadius}
              fill="none"
              stroke="url(#swipeProgressGrad)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
            <defs>
              <linearGradient id="swipeProgressGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#84cc16" />
                <stop offset="100%" stopColor="#4ade80" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-body text-sm font-bold text-ink leading-none">{sessionIndex}</span>
            <span className="font-body text-[9px] text-ink-muted leading-none mt-0.5">of {totalToRate}</span>
          </div>
        </div>
      </div>

      {/* Card stack - larger on desktop */}
      <div className="relative w-full flex justify-center mt-2">
        {/* Next card (behind) */}
        {nextRecipe && (
          <div className="absolute top-3 w-full flex justify-center" aria-hidden="true">
            <div className="w-full max-w-sm lg:max-w-md opacity-40 scale-[0.95] pointer-events-none">
              <SwipeCard recipe={nextRecipe} onSwipe={() => {}} active={false} showControls={false} />
            </div>
          </div>
        )}

        {/* Active card */}
        <div className="relative z-10 w-full max-w-sm lg:max-w-md mx-auto">
          <SwipeCard
            key={currentRecipe.id}
            recipe={currentRecipe}
            onSwipe={handleSwipe}
            onTap={(r) => setDetailRecipe(r)}
            onUndo={undoStack.length > 0 ? handleUndo : undefined}
            active={!showCoach}
          />
          {showCoach && <SwipeCoach onDismiss={dismissCoach} />}
        </div>
      </div>

      {/* Tip text - show keyboard shortcuts on desktop */}
      <p className="font-body text-xs text-ink-muted text-center mt-1">
        <span className="lg:hidden">Swipe right to like, left to skip</span>
        <span className="hidden lg:inline">Arrow keys (← skip, → like) or Z to undo</span>
      </p>

      {/* Recipe detail sheet */}
      {detailRecipe && (
        <RecipeDetail
          recipe={detailRecipe}
          onClose={() => setDetailRecipe(null)}
        />
      )}

      {matchMoment && (
        <MatchMoment
          recipe={matchMoment.recipe}
          members={matchMoment.members}
          triggerMember={matchMoment.triggerMember}
          onDone={() => setMatchMoment(null)}
        />
      )}
    </div>
  );
}
